import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api, photoUri } from "@/lib/api";
import { REPORT_REASONS } from "@/lib/constants";
import { fmtDate } from "@/lib/format";
import { useToast } from "@/components/toast";
import { AdminPage, Card, Gate, H, KV, Meta, reportTone } from "@/components/admin-ui";
import { Button, ErrorState, Loading, Pill } from "@/components/ui";

export default function AdminReportDetail() {
  return <Gate perm="reports" testId="admin-report-forbidden"><Inner /></Gate>;
}

const ACTS = [
  ["in_review", "Mark in review", "outline"], ["warn", "Warn member", "outline"], ["remove_photo", "Remove photo", "danger"],
  ["restrict", "Restrict", "outline"], ["suspend", "Suspend", "danger"], ["resolve", "Resolve", "primary"], ["dismiss", "Dismiss", "ghost"],
];

function Inner() {
  const toast = useToast();
  const qc = useQueryClient();
  const { id } = useParams();
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(null);
  const { data: r, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-report", id], queryFn: () => api(`/admin/reports/${id}`) });
  const act = async (action) => {
    setBusy(action);
    try {
      await api(`/admin/reports/${id}/action`, { method: "POST", body: { action, notes: notes || null } });
      toast("Report updated", "success");
      setNotes("");
      refetch();
      qc.invalidateQueries({ queryKey: ["admin-reports"] });
    } catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };
  if (isLoading) return <Loading />;
  if (isError || !r) return <ErrorState testId="admin-report-error" message={error?.message ?? ""} onRetry={() => refetch()} />;
  const closed = r.status === "resolved" || r.status === "dismissed";
  const visibleActs = ACTS.filter(([a]) => (a === "in_review" ? r.status === "open" : a === "remove_photo" ? !!r.photo_url : true));
  return (
    <AdminPage title="Report" testId="admin-report-screen">
      <div className="grid gap-3 lg:grid-cols-2">
        <Card testId="admin-report-summary">
          <div className="flex flex-wrap gap-1.5">
            <Pill testId="admin-report-status" label={r.status.replace("_", " ")} tone={reportTone(r.status)} />
            <Pill label={`${r.reports_against} report${r.reports_against === 1 ? "" : "s"} against this member`} tone="rose" />
          </div>
          <p className="my-2 font-display text-[26px]">{REPORT_REASONS[r.reason]}</p>
          <KV k="Reported member" v={`${r.reported.display_name} (${r.reported.account_status ?? "?"})`} />
          <KV k="Reporter" v={r.reporter.display_name} />
          <KV k="Submitted" v={fmtDate(r.created_at)} />
          <p className="mt-2 text-sm">{r.details || "No extra details."}</p>
        </Card>
        {r.photo_id ? (
          <Card testId="admin-report-photo">
            <p className="mb-2 text-sm font-bold">Reported photo</p>
            {r.photo_url ? <img src={photoUri(r.photo_url)} alt="Reported" className="max-h-80 w-full rounded-xl bg-[#F4EBE6] object-contain" /> : <Meta testId="admin-report-photo-removed">{r.photo_removed ? "Photo removed by moderation." : "Photo no longer available."}</Meta>}
          </Card>
        ) : null}
      </div>
      {closed ? (
        <Card testId="admin-report-resolution">
          <KV k="Outcome" v={r.resolution_action?.replace("_", " ")} /><KV k="By" v={r.resolved_by} /><KV k="When" v={fmtDate(r.resolved_at)} />
          <p className="mt-2 text-sm">{r.resolution_notes}</p>
        </Card>
      ) : (
        <Card testId="admin-report-actions">
          <label htmlFor="report-notes" className="text-sm font-bold">Resolution notes (required, warnings are sent to the member)</label>
          <textarea id="report-notes" data-testid="admin-report-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What did you find and decide?"
            className="mt-2 min-h-[100px] w-full rounded-xl border border-border p-3 text-[15px] focus:border-primary focus:outline-none" />
          <div className="mt-3 flex flex-wrap gap-2">
            {visibleActs.map(([a, label, v]) => <Button key={a} testId={`admin-report-${a.replace("_", "-")}`} title={label} size="sm" variant={v} loading={busy === a} onClick={() => act(a)} />)}
          </div>
        </Card>
      )}
      {r.other_reports.length ? (
        <>
          <H>Other reports against {r.reported.display_name}</H>
          {r.other_reports.map((o) => (
            <Card key={o.id} testId={`admin-report-other-${o.id}`}><p className="text-sm font-bold">{REPORT_REASONS[o.reason]} · {o.status.replace("_", " ")}</p><Meta>{fmtDate(o.created_at)}</Meta></Card>
          ))}
        </>
      ) : null}
    </AdminPage>
  );
}
